<template>
  <div>
    <SubmitalNavbar />

    <SubmitalHeader :submital="submital" page="documents" />

    <ContentView v-if="submital.id">
      <b-row class="mt-5">
        <b-col lg="4" sm="6">
          <p class="text-center fs-18 font-weight-bold text-primary">Subir documentos</p>
          <FileUploader
            :allowedFileTypes="['.xlsx', '.csv', '.pdf', '.dwg', '.ai']"
            :disabled="submitalLocked"
            @file-added="createDocument"
          />
        </b-col>
        <b-col lg="8" sm="6" class="border-left">
          <p class="text-center fs-18 font-weight-bold text-primary">Documentos subidos <sup>({{ documents.length }})</sup></p>
          <b-row v-if="fetchingDocuments" align-h="center">
            <div class="text-center text-secondary">
              <b-spinner small type="grow"></b-spinner>
              <span class="pl-2">Consultando documentos...</span>
            </div>
          </b-row>
          <b-row v-else>
            <b-col v-if="documents.length > 0 & !fetchingDocuments">
              <b-row v-for="(doc, index) in documents" :key="doc.id">
                <b-col cols="12">
                  <b-list-group>
                    <b-list-group-item class="mb-1">
                      <p v-if="doc.key_field" class="text-muted mb-0 fs-15">{{keyFieldsMap[doc.key_field] || doc.key_field}}</p>
                      <div class="d-flex justify-content-between align-items-center">
                        <b-link @click="downloadDocument(doc)">{{doc.name}}</b-link>
                        <Button :disabled="submitalLocked" variant="red" @click="onDeleteClick(doc, index)">
                          <font-awesome-icon :icon="['far', 'trash-alt']" fixed-width />
                        </Button>
                      </div>
                    </b-list-group-item>
                  </b-list-group>
                </b-col>
              </b-row>
            </b-col>
            <b-col v-else>
              <div class="text-center text-secondary">
                <span>No hay documentos para mostrar</span>
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </ContentView>

    <!-- CONFIRMATION: Delete-->
    <b-modal
      v-model="deleteConfirmationModal"
      centered
      no-close-on-backdrop
      no-close-on-esc
      hide-header
      hide-footer
      body-class="p-0"
      content-class="border-0 bg-transparent"
    >
      <div class="bg-white text-center shadow-lg" style="border-radius: 24px; overflow: hidden;">
        <div class="d-flex justify-content-end px-3 pt-3">
          <button type="button" class="close" aria-label="Cerrar" style="font-size: 28px;" @click="deleteConfirmationModal = false">&times;</button>
        </div>
        <div class="px-4 pb-4">
          <div class="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style="width: 88px; height: 88px; background-color: #fef2f2;">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" size="2x" class="text-danger" fixed-width />
          </div>
          <h5 class="font-weight-bold text-dark mb-2">¿Eliminar documento?</h5>
          <!-- <p v-if="selectedDocument" class="text-dark font-weight-bold text-break mb-2">
            {{ selectedDocument.name }}
          </p> -->
          <p class="text-secondary mb-4">
            El documento será eliminado completamente. La acción no se puede deshacer
          </p>
          <div class="d-flex justify-content-center">
            <button type="button" class="btn btn-secondary rounded-lg font-weight-bold px-4 mx-2" @click="deleteConfirmationModal = false">
              Cancelar
            </button>
            <button type="button" class="btn btn-danger rounded-lg font-weight-bold px-4 mx-2" :disabled="processing" @click="deleteDocument">
              {{ processing ? 'Borrando...' : 'Sí, borrar' }}
            </button>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- Alert -->
    <ToastAlert :show="alertShow" :variant="alertVariant" @close="alertShow = false">
      {{ alertMessage }}
    </ToastAlert>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { normalize, schema } from 'normalizr'

import SubmitalHeader from './SubmitalHeader'
import SubmitalNavbar from './SubmitalNavbar'

import ContentView from '../../shared/ContentView'
import Button from '../../shared/Button'
import FileUploader from '../../shared/FileUploader'
import ToastAlert from '../../shared/ToastAlert';


export default {
  name: 'SubmitalDocuments',
  components: { ContentView, SubmitalHeader, SubmitalNavbar, FileUploader, ToastAlert, Button },
  data () {
    return {
      id: Number(this.$route.params.id),
      processing: false,
      fetchingDocuments: false,
      deleteConfirmationModal: false,
      documentData: {},
      documentList: [],
      selectedDocument: null,
      selectedDocumentIndex: null,
      alertShow: false,
      alertVariant: null,
      alertMessage: '',
      keyFieldsMap: {
        'sandblasting_type': 'Diseño de Sanblasting',
        'polarized_type': 'Diseño de Polarizado'
      }
    }
  },
  computed: {
    ...mapState ({ submital: state => state.configurator.submital }),
    ...mapGetters ([
      'configurableTypeUrlString',
      'submitalLocked'
    ]),
    documents (){
      return this.documentList.map(id => this.documentData[id])
    },
  },
  mounted () {
    if (this.submital.id && this.submital.id === this.id) {
      this.fetchDocuments()
    } else {
      this.fetchSubmital()
    }
  },
  methods: {
    fetchSubmital () {
      this.$http.get(`/api/production/submitals/${this.id}`)
        .then((res) => {
          const { configurable, _quotes, ...submital } = res.data
          this.$store.commit('setConfigurable', configurable)
          this.$store.commit('setSubmital', submital)
          this.fetchDocuments()
        })
        .catch(e => {
          // console.log(e)
          this.$store.commit('setConfigurable', {})
          this.$store.commit('setSubmital', {})
        })
    },
    fetchDocuments () {
      if (this.processing) { return }
      this.processing = true
      this.fetchingDocuments = true
      this.$http.get(`/api/production/submitals/${this.submital.id}/documents`)
        .then((resp) => {
          // console.log('response document:', resp);
          this.normalizeDocuments(resp.data)
        })
        .catch(e => console.log(e))
        .finally(()=>{
          this.processing = false
          this.fetchingDocuments = false
        })
    },
    normalizeDocuments (files) {
      const data = normalize(
        { documents: files },
        { documents: [ new schema.Entity('documents') ] }
      )
      if (data.entities.hasOwnProperty('documents')) {
        this.documentData = data.entities.documents
      }
      this.documentList = data.result.documents
    },
    createDocument (form){
      if (this.processing) { return }
      this.processing = true
      this.$http.post(`/api/production/submitals/${this.submital.id}/documents`, {
        document: form
      })
        .then((resp) => {
          // console.log('response document:', resp);
          const createdDocument = resp.data.document
          this.$set(this.documentData, createdDocument.id, createdDocument)
          this.documentList.push(createdDocument.id)
        })
        .catch((e) => {
          console.log(e)
          this.alertVariant = 'danger'
          this.alertMessage = 'El archivo no se subió correctamente, archivos validos (.xlsx,.pdf)'
          this.alertShow = true
        })
        .finally(()=>{
          this.processing = false
        })
    },
    downloadDocument (doc){
      if (this.processing) { return }
      this.processing = true
      this.$http.get(`/api/production/submitals/${this.submital.id}/documents/${doc.id}/download`)
        .then((resp) => {
          // console.log(resp.data)
          const link = document.createElement('a')
          link.href = resp.data
          link.download = doc.name
          link.click()
        })
        .catch(e => console.log(e))
        .finally(this.processing = false)
    },
    onDeleteClick (doc, index){
      this.selectedDocument = doc
      this.selectedDocumentIndex = index
      this.deleteConfirmationModal = true
    },
    deleteDocument () {
      if (this.processing) { return }
      this.processing = true
      this.$http.delete(`/api/production/submitals/${this.submital.id}/documents/${this.selectedDocument.id}`)
        .then((resp) => {
          this.documentList.splice(this.selectedDocumentIndex, 1)
          delete this.documentData[this.selectedDocument.id]
          this.$store.commit('removeSubmitalDocument', this.selectedDocument.id)
        })
        .catch(e => console.log(e))
        .finally(()=> {
          this.deleteConfirmationModal = false
          this.processing = false
        })
    }
  }

}
</script>

<style scoped>
::v-deep .elegant-modal-content {
  border-radius: 24px;
  border: none;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
}

.delete-btn-gradient {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
  transition: all 0.2s ease;
}

.delete-btn-gradient:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
}

.delete-btn-gradient:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}
</style>